import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import { isValidThirdPartySerialNumber } from '@barkinglabs/nano-shared';
import { withBackBehavior, useBackAction } from '../lib/withBackBehavior';
import { resellerChipDetailsQuery, registerThirdPartyChip, recallChipMutation } from './ResellerChipDetails.graphql';
import NotFound from '../components/NotFound';
import { gqlTypes } from '../../types/gqlTypes';
import PetDetails from '../components/ChipDetails/PetDetails/PetDetails';
import { useQueryParams } from '../../lib/useQueryParams';
import QueryBasedPage, { QueryBasedPageChildProps } from '../components/QueryBasedPage';
import AcceptTransfer from '../components/AcceptTransfer/AcceptTransfer';
import ModalConfirm from '../components/modals/ModalConfirm';
import ModalAlert from '../components/modals/ModalAlert';
import EditPetAndContactInfo from '../components/EditPetAndContactInfo/EditPetAndContactInfo';
import { Links } from '../../lib/constants';
import { useAppViewContext } from '../lib/AppViewContext';
import { Redirect, useHistory } from 'react-router-dom';
import FinalizeTransferView from '../components/FinalizeTransferView/FinalizeTransferView';
import ContactDetails from '../components/ChipDetails/ContactDetails/ContactDetails';

interface ResellerChipDetailsQueryParams {
  id?: string;
}

interface ResellerChipDetailsData {
  chip: gqlTypes.resellerChip;
  ownedByReseller: boolean;
  canRecallAsReseller: boolean;
  isVerifiedRescue: boolean;
}

interface ResellerChipDetailsMainProps extends QueryBasedPageChildProps<ResellerChipDetailsData> {}

function ResellerChipDetailsMain({
  data: { chip, ownedByReseller, canRecallAsReseller, isVerifiedRescue },
  reload,
}: ResellerChipDetailsMainProps) {
  const history = useHistory();
  const { id: chipId, transfer, pet, latestContactHistoryEntry } = chip;
  const { isDesktop } = useAppViewContext();
  const [isEditing, setIsEditing] = useState(false);
  const { recallChip, isConfirmRecallModalOpen, setIsConfirmRecallModalOpen, recallError } = useRecallChip(
    chipId,
    reload,
  );

  const recallChipAndRedirect = async () => {
    await recallChip();

    return <Redirect to={Links.vetChipList()} />;
  };

  const canEdit = ownedByReseller;
  const showTransferResolution = !!transfer?.canResolve;

  if (transfer && showTransferResolution) {
    // Use transfer.pet if available, otherwise fall back to chip.pet
    const transferPet = transfer.pet ?? pet;
    return (
      <AcceptTransfer
        chip={chip}
        transfer={transfer}
        pet={transferPet}
        reload={() => reload()}
        canEdit={canEdit}
        isVerifiedRescue={isVerifiedRescue}
      />
    );
  }

  if (!latestContactHistoryEntry) {
    return <Redirect to={Links.register({ id: chipId })} />;
  }

  if (!pet && ownedByReseller) {
    // This case would be the reseller has a personal pet whose chip was owned by a different shelter.
    // They can still see them in the pet list, but they can't resolve the transfer.
    return (
      <FinalizeTransferView
        chipShortId={chip.shortId}
        contactInfo={latestContactHistoryEntry.contactInfo}
        onContactInfoUpdated={reload}
        onPetInfoUpdated={() => {
          reload();
          setIsEditing(false);
        }}
      />
    );
  }

  if (isEditing) {
    return (
      <EditPetAndContactInfo
        chipId={chipId}
        pet={pet ?? undefined}
        initialContactInfo={latestContactHistoryEntry?.contactInfo}
        onDone={() => {
          history.push(isDesktop ? Links.vetChipList() : Links.listChips());
        }}
      />
    );
  }

  if (!pet && !latestContactHistoryEntry?.contactInfo) {
    return <NotFound />;
  }

  return (
    <>
      {pet ? (
        <PetDetails
          chip={chip}
          pet={pet}
          contactInfo={latestContactHistoryEntry?.contactInfo}
          status={latestContactHistoryEntry ? 'active' : 'inactive'}
          canEdit={canEdit}
          onEditClicked={() => setIsEditing(!isEditing)}
          canRecallAsReseller={canRecallAsReseller}
          onRecallClicked={() => setIsConfirmRecallModalOpen(true)}
        />
      ) : (
        <ContactDetails
          chip={chip}
          contactInfo={latestContactHistoryEntry.contactInfo}
          canRecallAsReseller={canRecallAsReseller}
          onRecallClicked={() => setIsConfirmRecallModalOpen(true)}
        />
      )}
      <ModalConfirm
        message="Recalling a microchip clears the previous owner's information from the registration, while retaining the rescue's contact details as the primary backup."
        open={isConfirmRecallModalOpen}
        onAccept={async () => {
          await recallChipAndRedirect();
        }}
        onReject={() => setIsConfirmRecallModalOpen(false)}
      />
      <ModalAlert
        message={recallError ?? 'Unknown error occurred while recalling chip.'}
        open={!!recallError}
        onClose={() => setIsConfirmRecallModalOpen(false)}
      />
    </>
  );
}

const ResellerChipDetails = withBackBehavior('close', () => {
  const { id } = useQueryParams<ResellerChipDetailsQueryParams>();

  const transformData = useCallback((data: gqlTypes.resellerChipDetails): ResellerChipDetailsData | undefined => {
    const chip = data.resellerChip;
    const currentUserPetIds = data.currentUser.userHouseholds
      .flatMap((uh) => (uh.householdRole === gqlTypes.UserHouseholdRole.OWNER ? uh.household.pets : []))
      .map((pet) => pet.id);

    const chipAssignedToPetInDifferentHousehold = !!chip?.pet && !currentUserPetIds.includes(chip.pet.id);

    const latestContactHistoryMatchesCurrentUserEmail =
      chip?.latestContactHistoryEntry?.contactInfo.primaryEmail === data.currentUser.email;

    const isRescue = data.currentUser.chipReseller?.types?.includes(gqlTypes.ResellerType.RESCUE) ?? false;
    const isVerified = data.currentUser.chipReseller?.verified ?? false;
    const isVerifiedRescue = isRescue && isVerified;
    const canRecallAsReseller = isVerifiedRescue;

    return chip
      ? {
          chip,
          ownedByReseller: latestContactHistoryMatchesCurrentUserEmail && !chipAssignedToPetInDifferentHousehold,
          canRecallAsReseller,
          isVerifiedRescue,
        }
      : undefined;
  }, []);

  if (!id) {
    return <NotFound />;
  }

  return (
    <QueryBasedPage<gqlTypes.resellerChipDetails, gqlTypes.resellerChipDetailsVariables, ResellerChipDetailsData>
      query={resellerChipDetailsQuery}
      title={id}
      transformData={transformData}
      options={{ variables: { id } }}
    >
      {(childProps) => <ResellerChipDetailsMain {...childProps} />}
    </QueryBasedPage>
  );
});

const useRecallChip = (chipId: string, reload: () => Promise<void>) => {
  const [isConfirmRecallModalOpen, setIsConfirmRecallModalOpen] = useState(false);
  const [recallError, setRecallError] = useState<string | null>(null);

  const [resellerRecallChip] = useMutation<gqlTypes.resellerRecallChip, gqlTypes.resellerRecallChipVariables>(
    recallChipMutation,
    {
      variables: { input: { chipId } },
    },
  );

  const recallChip = useCallback(async () => {
    try {
      setRecallError(null);
      await resellerRecallChip();
      await reload();
    } catch (err) {
      setRecallError('Error recalling chip' + (err.message ? `: ${err.message}` : ''));
    } finally {
      setIsConfirmRecallModalOpen(false);
    }
  }, [resellerRecallChip, reload]);

  return { recallChip, isConfirmRecallModalOpen, setIsConfirmRecallModalOpen, recallError };
};

export default ResellerChipDetails;
